/* @jsx h */
import { useMenu } from "@app/vue/providers/index";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { computed, defineComponent, onMounted, onUnmounted, ref, h } from "vue";
import { useRoute, useRouter } from "vue-router";

export const ShopPaymentCallbackPage = defineComponent({
  name: "ShopPaymentCallbackPage",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { groupOrder, refetchGroupOrder } = useMenu();
    const { t } = useLocalization();

    const checkoutId = computed(() => route.params.checkoutId as string);
    const paymentGuid = computed(() => route.params.paymentGuid as string);
    const groupSlug = computed(() => route.params.groupSlug as string);
    const shopslug = computed(() => route.params.shopslug as string);
    const success = computed(() => route.query.success === "true");

    const isLoading = ref(true);
    const pollingInterval = ref<number | null>(null);

    const checkout = computed(() => {
      if (!groupOrder.value?.checkouts) return null;
      return groupOrder.value.checkouts.find((c) => c.id === checkoutId.value);
    });

    const checkAndRedirect = () => {
      if (!groupOrder.value) return;
      if (groupOrder.value.table) {
        if (checkout.value?.status === "confirmed") {
          router.push(
            `/restaurant/${shopslug.value}/group-order/${groupSlug.value}/checkout-status/${checkoutId.value}`,
          );
        }
      } else {
        if (checkout.value?.status === "completed") {
          // Stop polling and redirect
          if (pollingInterval.value !== null) {
            clearInterval(pollingInterval.value);
            pollingInterval.value = null;
          }

          router.push(
            `/restaurant/${shopslug.value}/group-order/${groupSlug.value}/checkout-result/${checkoutId.value}`,
          );
        }
      }
    };

    onMounted(async () => {
      await refetchGroupOrder();
      isLoading.value = false;

      // If payment was successful, start polling
      if (success.value) {
        // Check immediately
        checkAndRedirect();

        // Then poll every 3 seconds
        pollingInterval.value = window.setInterval(async () => {
          checkAndRedirect();
        }, 3000);
      }
    });

    onUnmounted(() => {
      if (pollingInterval.value !== null) {
        clearInterval(pollingInterval.value);
      }
    });

    return () => {
      if (isLoading.value) {
        return (
          <div class="flex-1 flex justify-center items-center min-h-screen">
            <Loading />
          </div>
        );
      }

      if (!success.value) {
        return (
          <div class="flex-1 flex flex-col justify-center items-center min-h-screen p-6">
            <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
              <div class="mb-4">
                <svg
                  class="w-16 h-16 mx-auto text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-gray-900 mb-2">
                {t("customer.payment_callback.payment_failed")}
              </h1>
              <p class="text-gray-600 mb-6">
                {t("customer.payment_callback.payment_failed_copy")}
              </p>
              <button
                onClick={() => router.back()}
                class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t("customer.payment_callback.go_back")}
              </button>
            </div>
          </div>
        );
      }

      // Payment successful - showing loading while polling
      return (
        <div class="flex-1 flex flex-col justify-center items-center min-h-screen p-6">
          <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div class="mb-4">
              <svg
                class="w-16 h-16 mx-auto text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-2">
              {t("customer.payment_callback.payment_successful")}
            </h1>
            <p class="text-gray-600 mb-6">
              {t("customer.payment_callback.processing_order")}
            </p>
            <Loading />
          </div>
        </div>
      );
    };
  },
});
